import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ExternalLink, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ErpSidebarContent } from "./ErpSidebarContent";
import { getAppItems } from "@/lib/config/navigationConfig";
import { useAppAccess } from "@/hooks/useAppAccess";
import { useSupportOnly } from "@/hooks/useSupportOnly";
import { useAuthStore } from "@/lib/store/authStore";

import { House } from "@phosphor-icons/react";
import type { ComponentType } from "react";

/** App name → PNG icon in /public/app-icons/ */
const APP_ICON_MAP: Record<string, string> = {
  "ธุรกิจ": "/app-icons/Business.png",
  "CRM": "/app-icons/CRM.png",
  "กล่องข้อความ": "/app-icons/กล่องข้อความ.png",
  "แชททีม": "/app-icons/แชททีม.png",
  "รายงาน": "/app-icons/รายงาน.png",
  "สร้างเว็บ": "/app-icons/สร้างเว็บ.png",
  "อีเมล": "/app-icons/อีเมล.png",
  "โน้ต": "/app-icons/โน้ต.png",
  "POS": "/app-icons/POS.png",
  "การจัดส่ง": "/app-icons/จัดส่ง.png",
  "แอป": "/app-icons/แอป.png",
  "บล็อก": "/app-icons/Blog.png",
};

/** Short names for the rail — matches ErpSidebarAppRail */
const SHORT_NAMES: Record<string, string> = {
  "หน้าหลัก": "หลัก",
  "ธุรกิจ": "ธุรกิจ",
  "CRM": "CRM",
  "กล่องข้อความ": "แชท",
  "รายงาน": "รายงาน",
  "สร้างเว็บ": "เว็บ",
  "อีเมล": "อีเมล",
  "โน้ต": "โน้ต",
  "การจัดส่ง": "จัดส่ง",
  "POS": "POS",
  "แอป": "แอป",
};

/** Strip tenant ID suffix from company name */
const TENANT_ID_SUFFIX_RE = /\s-\s[a-z0-9]{10}$/;
function stripTenantSuffix(name: string | null): string | null {
  return name ? name.replace(TENANT_ID_SUFFIX_RE, "") : name;
}

interface ErpMobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onExitComplete?: () => void;
  toggleButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export function ErpMobileDrawer({
  isOpen,
  onClose,
  onExitComplete,
  toggleButtonRef,
}: ErpMobileDrawerProps) {
  const drawerRef = useRef<HTMLElement>(null);
  const reducedMotion = useReducedMotion();
  const animationDuration = reducedMotion ? 0 : 0.3;
  const location = useLocation();

  const rawCompany = useAuthStore((s) => s.tenantCompany);
  const tenantCompany = stripTenantSuffix(rawCompany);
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);

  // App rail items
  const allApps = getAppItems();
  const { hasAccess, isInstalled } = useAppAccess();
  const supportOnly = useSupportOnly();
  const apps = supportOnly
    ? []
    : allApps.filter((app) => {
        if (app.appRegistryKey && !isInstalled(app.appRegistryKey)) return false;
        if (app.appAccessKey && !hasAccess(app.appAccessKey)) return false;
        return true;
      });

  // Focus management
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        const firstFocusable =
          drawerRef.current?.querySelector<HTMLElement>("button, a");
        firstFocusable?.focus();
      });
    } else {
      toggleButtonRef.current?.focus();
    }
  }, [isOpen, toggleButtonRef]);

  // Escape key closes drawer
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  return (
    <AnimatePresence onExitComplete={onExitComplete}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: animationDuration }}
            className="fixed inset-0 z-[35] bg-black/40"
            aria-hidden="true"
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.aside
            ref={drawerRef}
            id="erp-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="เมนู"
            initial={{ x: "-85vw" }}
            animate={{ x: 0 }}
            exit={{ x: "-85vw" }}
            transition={{
              duration: animationDuration,
              ease: [0.16, 1, 0.3, 1],
            }}
            className={cn(
              "fixed top-0 left-0 bottom-0 z-40",
              "flex flex-col",
              
            )}
            style={{
              width: "85vw",
              background: "#ffffff",
              boxShadow:
                "rgba(0, 0, 0, 0.05) 0px 4px 12px, rgba(0, 0, 0, 0.03) 0px 8px 24px",
            }}
          >
            {/* Header — company info + close */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 shrink-0">
              <div className="w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-800 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 truncate leading-snug">
                  {tenantCompany || "My Company"}
                </p>
                {storefrontUrl && (
                  <a
                    href={storefrontUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 hover:text-gray-600 truncate leading-snug mt-0.5 transition-colors"
                  >
                    <span className="truncate">{storefrontUrl.replace(/^https?:\/\//, "")}</span>
                    <ExternalLink className="w-3 h-3 shrink-0" />
                  </a>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
                aria-label="ปิดเมนู"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* App Rail — horizontal scrollable row (hidden for support-only tenants) */}
            {!supportOnly && (
              <div className="shrink-0 border-b border-gray-200/40 dark:border-gray-700/40">
                <div className="flex gap-1 px-3 py-2 overflow-x-auto scrollbar-none">
                  {/* Home */}
                  <MobileRailItem
                    href="/"
                    name="หน้าหลัก"
                    appName={null}
                    isActive={location.pathname === "/"}
                    onClick={onClose}
                  />
                  {apps.map((app) => {
                    const isActive = app.href ? location.pathname.startsWith(app.href) : false;
                    return (
                      <MobileRailItem
                        key={app.name}
                        href={app.href ?? "/"}
                        name={app.name}
                        appName={app.name}
                        fallbackIcon={app.icon}
                        isActive={isActive}
                        onClick={onClose}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Panel content — shared with desktop sidebar */}
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none">
              <ErpSidebarContent />
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function MobileRailItem({
  href,
  name,
  appName,
  fallbackIcon: _FallbackIcon,
  isActive,
  onClick,
}: {
  href: string;
  name: string;
  /** Pass app name to look up APP_STYLE, or null for Home */
  appName: string | null;
  fallbackIcon?: ComponentType<{ className?: string; strokeWidth?: number }>;
  isActive: boolean;
  onClick: () => void;
}) {
  const shortName = SHORT_NAMES[name] ?? name;
  const iconSrc = appName ? APP_ICON_MAP[appName] : null;

  return (
    <Link
      to={href}
      onClick={onClick}
      className="flex flex-col items-center justify-center shrink-0 w-[54px] py-1.5"
    >
      {iconSrc ? (
        <img src={iconSrc} alt={name} className="w-9 h-9 object-contain" loading="lazy" />
      ) : (
        /* Home icon */
        <div
          className={cn(
            "flex items-center justify-center w-9 h-9 rounded-[10px] transition-all duration-200",
            isActive
              ? "bg-gray-900/10 dark:bg-white/15"
              : "bg-gray-500/10 dark:bg-white/[0.08]"
          )}
        >
          <House
            size={20}
            weight="fill"
            className={cn(
              "transition-transform duration-200",
              isActive ? "text-gray-900 dark:text-gray-100 scale-110" : "text-gray-600 dark:text-gray-400"
            )}
          />
        </div>
      )}
      <span
        className={cn(
          "text-[10px] leading-tight mt-1 truncate max-w-[50px]",
          isActive
            ? "font-semibold text-gray-900 dark:text-gray-100"
            : "text-gray-500 dark:text-gray-400 font-medium"
        )}
      >
        {shortName}
      </span>
    </Link>
  );
}
